import React, { useMemo } from 'react';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Tooltip from '@mui/material/Tooltip';
import './CRSTrendArrow.css';

const MIN_DATA_POINTS = 3;
const SLOPE_THRESHOLD = 0.05;
const CRS_CHANGE_THRESHOLD = 0.3;

const calculateTrendFromHistory = (historyArray) => {
    if (!historyArray || historyArray.length < MIN_DATA_POINTS) {
        return {
            hasTrend: false,
            slope: 0,
            crsChange: 0,
            percentChange: 0,
            direction: 'neutral'
        };
    }

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const last7DaysData = historyArray.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= sevenDaysAgo;
    });
    
    if (last7DaysData.length < MIN_DATA_POINTS) {
        return {
            hasTrend: false,
            slope: 0,
            crsChange: 0,
            percentChange: 0,
            direction: 'neutral'
        };
    }

    const sortedData = [...last7DaysData].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA.getTime() - dateB.getTime();
    });

    if (sortedData.length === 0) {
        return {
            hasTrend: false,
            slope: 0,
            crsChange: 0,
            percentChange: 0,
            direction: 'neutral'
        };
    }

    const numericData = sortedData
        .map(d => ({ ...d, crsValue: parseFloat(d.crsScore) }))
        .filter(d => !isNaN(d.crsValue));

    if (numericData.length < MIN_DATA_POINTS) {
        return {
            hasTrend: false,
            slope: 0,
            crsChange: 0,
            percentChange: 0,
            direction: 'neutral'
        };
    }

    const oldestCrs = numericData[0].crsValue;
    const recentCrs = numericData[numericData.length - 1].crsValue;
    const n = numericData.length;
    
    const crsChange = recentCrs - oldestCrs;
    const percentChange = oldestCrs > 0 ? (crsChange / oldestCrs) * 100 : 0;
    
    const last3Days = new Date();
    last3Days.setDate(last3Days.getDate() - 3);

    const recentData = numericData.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= last3Days;
    });
    
    let recentCrsChange = 0;
    let recentOldestCrs = recentCrs;
    
    if (recentData.length >= 3) {
        const firstThird = Math.floor(recentData.length / 3);
        const lastThird = Math.floor(recentData.length * 2 / 3);
        
        const earlyRecent = recentData.slice(0, firstThird + 1);
        const lateRecent = recentData.slice(lastThird);
        
        const earlyAvg = earlyRecent.reduce((sum, d) => sum + d.crsValue, 0) / earlyRecent.length;
        const lateAvg = lateRecent.reduce((sum, d) => sum + d.crsValue, 0) / lateRecent.length;
        
        recentCrsChange = lateAvg - earlyAvg;
        recentOldestCrs = earlyAvg;
    } else if (recentData.length >= 2) {
        recentOldestCrs = recentData[0].crsValue;
        const recentRecentCrs = recentData[recentData.length - 1].crsValue;
        recentCrsChange = recentRecentCrs - recentOldestCrs;
    }
    
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    numericData.forEach((item, index) => {
        const x = index;
        const y = item.crsValue;
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumX2 += x * x;
    });

    const denominator = (n * sumX2 - sumX * sumX);
    let slope = 0;
    if (denominator !== 0) {
        slope = (n * sumXY - sumX * sumY) / denominator;
    }

    const minCrs = Math.min(...numericData.map(d => d.crsValue));
    const minIndex = numericData.findIndex(d => d.crsValue === minCrs);
    
    const recoveryFromMin = recentCrs - minCrs;
    
    let direction = 'neutral';
    
    if (recentData.length >= 2 && Math.abs(recentCrsChange) >= 0.2) {
        direction = recentCrsChange > 0 ? 'up' : 'down';
    } else if (recoveryFromMin >= 0.3 && minIndex < numericData.length - 3) {
        direction = 'up';
    } else if (Math.abs(crsChange) >= CRS_CHANGE_THRESHOLD) {
        direction = crsChange > 0 ? 'up' : 'down';
    } else if (Math.abs(slope) > SLOPE_THRESHOLD) {
        direction = slope > 0 ? 'up' : 'down';
    }

    
    return {
        hasTrend: direction !== 'neutral',
        slope: parseFloat(slope.toFixed(4)),
        crsChange: parseFloat(crsChange.toFixed(2)),
        percentChange: parseFloat(percentChange.toFixed(2)),
        direction
    };
};

const CRSTrendArrow = ({ crsHistory }) => {
    const trendData = useMemo(
        () => calculateTrendFromHistory(crsHistory),
        [crsHistory]
    );

    if (!crsHistory || crsHistory.length === 0) {
        return null;
    }

    if (trendData.direction === 'up') {
        const tooltipText = `CRS increased by ${trendData.crsChange.toFixed(2)} points (${trendData.percentChange > 0 ? '+' : ''}${trendData.percentChange.toFixed(1)}%)`;
        return (
            <Tooltip title={tooltipText} arrow placement="top">
                <span className="crs-trend-arrow up" role="img" tabIndex={0} aria-label={tooltipText}>
                    <ArrowUpward fontSize="small" />
                </span>
            </Tooltip>
        );
    } else if (trendData.direction === 'down') {
        const tooltipText = `CRS decreased by ${Math.abs(trendData.crsChange).toFixed(2)} points (${trendData.percentChange.toFixed(1)}%)`;
        return (
            <Tooltip title={tooltipText} arrow placement="top">
                <span className="crs-trend-arrow down" role="img" tabIndex={0} aria-label={tooltipText}>
                    <ArrowDownward fontSize="small" />
                </span>
            </Tooltip>
        );
    }

    return null;
};

export default CRSTrendArrow;
